import { type ReadonlyURLSearchParams } from 'next/navigation';

import {
  LISTINGS_DEFAULT_SEARCH_PARAMS,
  LISTINGS_PER_PAGE,
  LISTINGS_PER_PAGE_MAP,
} from '@/features/listing/constants/listings-filters-constants';
import {
  type FiltersParams,
  MapParamsNames,
} from '@/features/listing/types/listing-filters-types';
import {
  ExtraFieldsNames,
  FiltersNames,
  GeoBboxNames,
  PaginationNames,
} from '@/features/listing/types/listing-filters-types';
import { type ListingType } from '@/features/listing/types/listing-types';
import { type Place } from '@/features/place/types/place-types';
import convertGeoBoundsWithMinimalRadius from '@/features/place/utils/convert-geo-bounds-with-minimal-radius';

export default function prepareListingsSearchParams(
  searchParams: ReadonlyURLSearchParams | URLSearchParams,
  listingType: ListingType,
  placeBbox: Place['bbox'],
  isMap = false,
): URLSearchParams {
  const currentSearchParams: Partial<FiltersParams> = {};
  const params = new URLSearchParams(searchParams);

  // Need to filter out any invalid search params that not applicable to listings
  params.forEach((value, key) => {
    // @ts-expect-error - TS doesn't know that key is a valid SearchParams key
    if (value && Object.values(FiltersNames).includes(key)) {
      // @ts-expect-error - TS doesn't know that key is a valid SearchParams key
      currentSearchParams[key] = value;
    }
  });

  const latitudeSouthWestParams = Number(params.get(GeoBboxNames.LatitudeSouthWest));
  const latitudeNorthEastParams = Number(params.get(GeoBboxNames.LatitudeNorthEast));
  const longitudeNorthEastParams = Number(params.get(GeoBboxNames.LongitudeNorthEast));
  const longitudeSouthWestParams = Number(params.get(GeoBboxNames.LongitudeSouthWest));

  const convertedBbox = convertGeoBoundsWithMinimalRadius({
    latitudeNorthEast: latitudeNorthEastParams || placeBbox.latitudeNorthEast,
    latitudeSouthWest: latitudeSouthWestParams || placeBbox.latitudeSouthWest,
    longitudeNorthEast: longitudeNorthEastParams || placeBbox.longitudeNorthEast,
    longitudeSouthWest: longitudeSouthWestParams || placeBbox.longitudeSouthWest,
  });

  const sharedSearchParams: Record<string, string> = {
    ...LISTINGS_DEFAULT_SEARCH_PARAMS,
    ...currentSearchParams,
    [FiltersNames.ListingType]: listingType,
    [GeoBboxNames.LatitudeSouthWest]: String(convertedBbox.latitudeSouthWest),
    [GeoBboxNames.LongitudeSouthWest]: String(convertedBbox.longitudeSouthWest),
    [GeoBboxNames.LatitudeNorthEast]: String(convertedBbox.latitudeNorthEast),
    [GeoBboxNames.LongitudeNorthEast]: String(convertedBbox.longitudeNorthEast),
    [PaginationNames.PageSize]: params.get(PaginationNames.PageSize) ?? String(LISTINGS_PER_PAGE),
  };

  const page = params.get(PaginationNames.PageNumber) ? Number(params.get(PaginationNames.PageNumber)) : 1;

  if (isMap) {
    sharedSearchParams[MapParamsNames.MapData] = 'true';
    sharedSearchParams[PaginationNames.PageSize] = String(LISTINGS_PER_PAGE_MAP);
  }

  return new URLSearchParams({
    ...sharedSearchParams,
    [ExtraFieldsNames.IncludeAvgRent]: 'true',
    [ExtraFieldsNames.IncludeTotalCount]: 'true',
    [PaginationNames.PageNumber]: String(page),
  });
}
